import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useToast, ToastContainer } from '../components/Toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { MapPin } from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { Monitor, Plus, Play, Trash2, Upload, Calendar, Globe, Clock, AlertTriangle, Power, LogOut, Users, List, Video, QrCode, Maximize2, Folder, FolderPlus, X, Settings, Building2, Edit2, FileText, Store, Zap, AlertCircle, Tv, School, Hospital, Hotel, Factory, Landmark, DoorOpen, ShoppingBag, Building, HardHat, Target, LayoutDashboard, Images, ListVideo,Crown, CreditCard } from 'lucide-react';
import PlaylistManager from '../components/PlaylistManager';
import { useWebSocket } from '../hooks/useWebSocket';
import { BarChart3 } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ScreenHealthMonitor from '../components/ScreenHealthMonitor';
import OrganizationSettings from '../components/OrganizationSettings';
import EnhancedMediaLibrary from '../components/EnhancedMediaLibrary';
import AdvancedScheduler from '../components/AdvancedScheduler';
import FeatureMarketplace from '../components/FeatureMarketplace';
import WidgetManager from '../components/WidgetManager';
import MobileNav from '../components/MobileNav';
import StripeCheckout from '../components/StripeCheckout';
import TierBadge from '../components/TierBadge';
import UpgradeModal from '../components/UpgradeModal';
import AddScreenModal from '../components/AddScreenModal';
import EditScreenModal from '../components/modals/EditScreenModal';
import ScreenConnectionInfo from '../components/ScreenConnectionInfo';
import ScreenConnectionModal from '../components/ScreenConnectionModal';
import Analytics from './Analytics';
import SuperAdminPanel from '../components/SuperAdminPanel';
import { authUtils } from '../utils/auth';
import SetupWizard from '../components/SetupWizard/SetupWizard';
import ContentStudio from '../components/ContentStudio/ContentStudio';

// Debounce helper
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

const ScreenCard = memo(({ 
  screen, 
  isOnline, 
  timeSince, 
  onOpenPlayer, 
  onEdit, 
  onShowQR, 
  onDelete,
  groups,
  playlists,
  t 
}) => {
  console.log('🎨 ScreenCard render:', screen.name);
  
  return (
    <div 
      className={`screen-card-mobile ${
        isOnline 
          ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' 
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      } border rounded-lg p-4 transition-all hover:shadow-md`}
    >
      {/* Mobile: Vertical Layout - OPTIMIZED */}
      <div className="md:hidden space-y-1.5">
        {/* Header Row: Icon + Name + Status */}
        <div className="flex items-start gap-2.5">
          <div className={`w-10 h-10 ${
            isOnline ? 'bg-blue-100 dark:bg-blue-900' : 'bg-red-100 dark:bg-red-900'
          } rounded-lg flex items-center justify-center flex-shrink-0`}>
            <Monitor 
              className={`${isOnline ? 'text-primary-600 dark:text-primary-400' : 'text-red-600 dark:text-red-400'}`}
              size={20}
            />
          </div>
          
          <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="text-base font-bold text-gray-900 dark:text-gray-100 truncate leading-tight">
                {screen.name}
              </h4>
              {screen.location && (
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  <MapPin size={11} className="flex-shrink-0" />
                  <span className="truncate">{screen.location}</span>
                </div>
              )}
            </div>
            
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
              isOnline 
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></span>
              {isOnline ? t('dashboard.online') : t('dashboard.offline')}
            </span>
          </div>
        </div>

        {/* Info Row */}
        <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400 ml-12">
          <div className="flex items-center gap-1">
            <Users size={12} className="flex-shrink-0" />
            <span className="truncate">{groups.find(g => g.id === screen.group_id)?.name || t('screens.noGroup')}</span>
          </div>
          <span className="text-gray-400 dark:text-gray-600">•</span>
          <div className="flex items-center gap-1">
            <List size={12} className="flex-shrink-0" />
            <span className="truncate">{playlists.find(p => p.id === screen.playlist_id)?.name || 'No playlist'}</span>
          </div>
        </div>
        
        {/* Offline Info */}
        {!isOnline && (
          <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-medium ml-12">
            <AlertCircle size={12} />
            <span>{t('dashboard.offlineSince')} {timeSince}</span>
          </div>
        )}

        {/* Action Buttons - 4 ICONS IN ROW */}
        <div className="grid grid-cols-4 gap-1.5 pt-0.5">
          <button 
            onClick={() => onOpenPlayer(screen.uuid)}
            className="flex items-center justify-center bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white p-2.5 rounded-lg transition-colors"
            title={t('screens.openPlayer')}
          >
            <Play size={18} />
          </button>
          
          <button 
            onClick={() => onEdit(screen)}
            className="flex items-center justify-center bg-gray-200 hover:bg-gray-300 active:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 p-2.5 rounded-lg transition-colors"
            title={t('screens.edit')}
          >
            <Edit2 size={18} />
          </button>
          
          <button 
            onClick={() => onShowQR(screen)}
            className="flex items-center justify-center bg-blue-100 hover:bg-blue-200 active:bg-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 p-2.5 rounded-lg transition-colors"
            title={t('screens.showQR')}
          >
            <QrCode size={18} />
          </button>
          
          <button 
            onClick={() => onDelete(screen.id)}
            className="flex items-center justify-center bg-red-100 hover:bg-red-200 active:bg-red-300 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 p-2.5 rounded-lg transition-colors"
            title={t('screens.delete')}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Desktop: Horizontal Compact Layout */}
      <div className={`hidden md:flex items-center justify-between gap-4 bg-white dark:bg-gray-800 border rounded-xl p-5 transition-all hover:shadow-lg ${
        isOnline 
          ? 'border-gray-200 dark:border-gray-700' 
          : 'border-red-200 dark:border-red-800 border-l-4 border-l-red-500'
      }`}>
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-14 h-14 ${
            isOnline ? 'bg-blue-100 dark:bg-blue-900' : 'bg-red-100 dark:bg-red-900'
          } rounded-xl flex items-center justify-center flex-shrink-0`}>
            <Monitor 
              className={`${isOnline ? 'text-primary-600 dark:text-primary-400' : 'text-red-600 dark:text-red-400'}`}
              size={24}
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1.5">
              <div className="min-w-0">
                <h4 className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
                  {screen.name}
                </h4>
                {screen.location && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    <MapPin size={12} className="flex-shrink-0" />
                    <span className="truncate">{screen.location}</span>
                  </div>
                )}
              </div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                isOnline 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              }`}>
                <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></span>
                {isOnline ? t('dashboard.online') : t('dashboard.offline')}
              </span>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              {isOnline ? (
                <>
                  <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <Users size={14} />
                    <span className="truncate">
                      {groups.find(g => g.id === screen.group_id)?.name || t('screens.noGroup')}
                    </span>
                  </div>
                  <span className="text-gray-400 dark:text-gray-600">•</span>
                  <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <List size={14} />
                    <span className="truncate">
                      {playlists.find(p => p.id === screen.playlist_id)?.name || 'No playlist'}
                    </span>
                  </div>
                </>
              ) : (
                <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400 font-medium">
                  <AlertCircle size={16} />
                  {t('dashboard.offlineSince')} {timeSince}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button 
            onClick={() => onOpenPlayer(screen.uuid)}
            className="p-2.5 hover:bg-primary-100 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg transition-colors"
            title={t('screens.openPlayer')}
          >
            <Play size={18} />
          </button>
          
          <button 
            onClick={() => onShowQR(screen)}
            className="p-2.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg transition-colors"
            title={t('screens.showQR')}
          >
            <QrCode size={18} />
          </button>
          
          <button 
            onClick={() => onEdit(screen)}
            className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg transition-colors"
            title={t('screens.edit')}
          >
            <Edit2 size={18} />
          </button>
          
          <button 
            onClick={() => onDelete(screen.id)}
            className="p-2.5 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg transition-colors"
            title={t('screens.delete')}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: Nur re-rendern wenn sich relevante Props ändern
  return (
    prevProps.screen.id === nextProps.screen.id &&
    prevProps.screen.name === nextProps.screen.name &&
    prevProps.screen.location === nextProps.screen.location &&
    prevProps.screen.status === nextProps.screen.status &&
    prevProps.screen.last_seen === nextProps.screen.last_seen &&
    prevProps.screen.group_id === nextProps.screen.group_id &&
    prevProps.screen.playlist_id === nextProps.screen.playlist_id &&
    prevProps.isOnline === nextProps.isOnline &&
    prevProps.timeSince === nextProps.timeSince
  );
});

ScreenCard.displayName = 'ScreenCard';

const Dashboard = () => {
  console.log('🏠 Dashboard RENDER');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toasts, addToast, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [screens, setScreens] = useState([]);
  const [media, setMedia] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [users, setUsers] = useState([]);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [token, setToken] = useState(null);
  
  const [isTypingInModal, setIsTypingInModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showSetupWizard, setShowSetupWizard] = useState(false);

const userRole = currentUser?.role || localStorage.getItem('userRole');
const canManageUsers = userRole === 'admin' || userRole === 'super_admin';
  
  // ✨ Load auth data on mount
  useEffect(() => {
    const token = authUtils.getToken();
    const user = authUtils.getUser();
    
    if (token && user) {
      console.log('✅ Auth data loaded');
      setToken(token);
      setCurrentUser(user);
    }
  }, []);

  // 🚀 Check onboarding status for new users
useEffect(() => {
  const checkOnboarding = async () => {
    try {
      const savedToken = localStorage.getItem('token');
      if (!savedToken) return;
      
      const response = await fetch(`${API_URL}/auth/onboarding-status`, {
        headers: { 'Authorization': `Bearer ${savedToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (!data.onboarding_completed) {
          setShowSetupWizard(true);
        }
      }
    } catch (error) {
      console.error('Error checking onboarding:', error);
    }
  };
  
  if (token && currentUser) {
    checkOnboarding();
  }
}, [token, currentUser]);
  
  const [trialInfo, setTrialInfo] = useState(null);

  const [showAddMediaModal, setShowAddMediaModal] = useState(false);
  const [showAddScreenModal, setShowAddScreenModal] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [newlyCreatedScreen, setNewlyCreatedScreen] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedScreenForQR, setSelectedScreenForQR] = useState(null);

  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [upgradeReason, setUpgradeReason] = useState('general');

  const iconOptions = [
  { id: 'monitor', component: Monitor, label: 'Monitor' },
  { id: 'tv', component: Tv, label: 'TV' },
  { id: 'building2', component: Building2, label: 'Office' },
  { id: 'store', component: Store, label: 'Store' },
  { id: 'school', component: School, label: 'School' },
  { id: 'hospital', component: Hospital, label: 'Hospital' },
  { id: 'hotel', component: Hotel, label: 'Hotel' },
  { id: 'factory', component: Factory, label: 'Factory' },
  { id: 'landmark', component: Landmark, label: 'Bank' },
  { id: 'dooropen', component: DoorOpen, label: 'Entrance' },
  { id: 'shoppingbag', component: ShoppingBag, label: 'Mall' },
  { id: 'building', component: Building, label: 'Building' },
  { id: 'hardhat', component: HardHat, label: 'Construction' },
  { id: 'globe', component: Globe, label: 'Global' },
  { id: 'target', component: Target, label: 'Target' }
];
    // ✅ HIER EINFÜGEN: Backward-compatible Icon Renderer
  const renderGroupIcon = (iconId, size = 20, className = '') => {
    // Fallback: Kein Icon vorhanden
    if (!iconId) {
      return <Monitor size={size} className={className} />;
    }
    
    // Backward compatibility: Alter Emoji-String (1-2 Zeichen)
    if (typeof iconId === 'string' && iconId.length <= 2) {
      return <span className={`text-xl ${className}`}>{iconId}</span>;
    }
    
    // Neuer Lucide Icon Renderer
    const iconOption = iconOptions.find(opt => opt.id === iconId);
    const IconComponent = iconOption ? iconOption.component : Monitor;
    return <IconComponent size={size} className={className} />;
  };
  
  // ============================================
  // SUBSCRIPTION MANAGEMENT
  // ============================================
  const handleManageSubscription = async () => {
    try {
      const savedToken = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${savedToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        console.error('Failed to create portal session');
        alert('Fehler beim Öffnen des Abo-Portals');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      alert('Fehler beim Öffnen des Abo-Portals');
    }
  };

  // NEU: Edit Screen States
  const [editingScreen, setEditingScreen] = useState(null);

  const [newMedia, setNewMedia] = useState({ name: '', type: 'image', url: '', duration: 10 });
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'user' });
  
  const [uploadMethod, setUploadMethod] = useState('url');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsUser, setSettingsUser] = useState(null);
  const [viewingAsOrganization, setViewingAsOrganization] = useState(null);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  
  const [needsReload, setNeedsReload] = useState(false);
  const hasLoadedSuperAdminData = useRef(null);
  const loadDataTimeoutRef = useRef(null);

// ============================================
  // LOAD DATA FUNCTION (useCallback)
  // ============================================
  const loadDataInternal = useCallback(async () => {
    try {
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        setIsAuthenticated(false);
        return;
      }

      const userRole = localStorage.getItem('userRole');
      const viewingOrgId = localStorage.getItem('viewingAsOrgId');

      console.log('🔄 loadData called');
      console.log('   👤 userRole:', userRole);
      console.log('   🏢 viewingOrgId:', viewingOrgId);
      console.log('   Will filter:', viewingOrgId && userRole === 'super_admin' ? 'YES' : 'NO');

      // Build URL mit optional filter
      const buildUrl = (endpoint) => {
        if (viewingOrgId && userRole === 'super_admin') {
          const url = `${API_URL}${endpoint}?filter_org=${viewingOrgId}`;
          console.log('   🔍 Filtered URL:', url);
          return url;
        }
        const url = `${API_URL}${endpoint}`;
        console.log('   🔍 Normal URL:', url);
        return url;
      };

      const requests = [
        fetch(buildUrl('/screens'), { headers: { Authorization: `Bearer ${savedToken}` } }),
        fetch(buildUrl('/media'), { headers: { Authorization: `Bearer ${savedToken}` } }),
        fetch(buildUrl('/playlists'), { headers: { Authorization: `Bearer ${savedToken}` } }),
        fetch(buildUrl('/groups'), { headers: { Authorization: `Bearer ${savedToken}` } })
      ];

      if (userRole === 'admin' || userRole === 'super_admin') {
        requests.push(fetch(buildUrl('/users'), { headers: { Authorization: `Bearer ${savedToken}` } }));
      }

      const responses = await Promise.all(requests);

      const screensData = await responses[0].json();

      console.log('🖥️ Raw screens from backend:', screensData.map(s => ({
        name: s.name,
        status: s.status,
        real_status: s.real_status,
        last_seen: s.last_seen
      })));

      const now = new Date();
      // Backend liefert bereits korrekten Status via real_status
      const correctedScreens = screensData.map(screen => ({
        ...screen,
        status: screen.real_status || screen.status || 'offline'
      }));

      console.log('Loaded screens:', correctedScreens.map(s => ({
        name: s.name,
        status: s.status,
        last_seen: s.last_seen
      })));

      const mediaData = await responses[1].json();
      const playlistsData = await responses[2].json();
      const groupsData = await responses[3].json();
      const usersData = (userRole === 'admin' || userRole === 'super_admin') && responses[4] ? await responses[4].json() : [];

      console.log('📊 Data loaded:', {
        screens: screensData.length,
        media: mediaData.length,
        playlists: playlistsData.length,
        groups: groupsData.length,
        users: Array.isArray(usersData) ? usersData.length : 0,
        filteredBy: viewingOrgId || 'all'
      });

      setScreens(Array.isArray(correctedScreens) ? correctedScreens : []);
      setMedia(Array.isArray(mediaData) ? mediaData : []);
      setPlaylists(Array.isArray(playlistsData) ? playlistsData : []);
      setGroups(Array.isArray(groupsData) ? groupsData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);

      // ============================================
      // TRIAL INFO LADEN (NEU!)
      // ============================================
      // Fetch trial info from backend (includes null check for Super Admin)
      const fetchTrialInfo = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            
            console.log('⏰ Trial Info from backend:', data.trialInfo);
            
            // trialInfo can be null for Super Admin!
            if (data.trialInfo) {
              setTrialInfo({
                daysLeft: data.trialInfo.daysLeft,
                isExpired: data.trialInfo.status === 'expired',
                trialEndDate: new Date(data.trialInfo.endDate),
                status: data.trialInfo.status
              });
            } else {
              // Super Admin or no trial - don't show badge
              setTrialInfo(null);
            }
          }
        } catch (error) {
          console.error('Error fetching trial info:', error);
          setTrialInfo(null);
        }
      };

      fetchTrialInfo();

      console.log('State updated');
    } catch (error) {
      console.error('⚠️ Error loading data:', error);
      setScreens([]);
      setMedia([]);
      setPlaylists([]);
      setGroups([]);
      setUsers([]);
    }
  }, [currentUser]); // currentUser als dependency!

  // Debounced version
  const loadData = useCallback(
    debounce(loadDataInternal, 0),
    [loadDataInternal]
  );

  useEffect(() => {
    if (needsReload) {
      console.log('🔄 Reloading data due to changes...');
      loadData();
      setNeedsReload(false);
    }
  }, [needsReload, loadData]);

// ============================================
  // WEBSOCKET HANDLER - HYBRID + TOAST NOTIFICATIONS
  // ============================================
  const handleWebSocketMessage = useCallback((message) => {
    console.log('📨 Dashboard received:', message.type);
    
    // Instant Updates für kritische Events (ohne Debounce)
    switch (message.type) {
      case 'screen_status':
      case 'screen-status-update':
        console.log('📡 Instant screen status update:', message.data);
        setScreens(prev => prev.map(s => {
          if (s.uuid === message.data.uuid || s.id === message.data.id || s.id === message.data.screenId) {
            console.log(`✅ Updating screen ${s.name}: ${s.status} → ${message.data.status}`);
            return {
              ...s,
              status: message.data.status,
              last_seen: message.data.lastSeen,
              lastSeen: message.data.lastSeen
            };
          }
          return s;
        }));
        // Status updates brauchen kein full reload
        return;
      
      case 'screen_registered':
  console.log('🎉 Device registered:', message.data);
  addToast({
    title: t('dashboard.deviceConnected', 'Gerät verbunden!'),
    description: `${message.data?.screenName || t('dashboard.aDevice', 'Ein Gerät')} wartet auf Inhalte. Erstellen Sie Medien und Playlists im Dashboard.`
  }, 'device', 6000);
  
  loadDataInternal();
  return;
        
        // Reload to show the newly registered screen
        loadDataInternal();
        return;
        
      case 'screen_deleted':
        setScreens(prev => prev.filter(s => s.id !== message.data.id));
        return;
        
      case 'media_deleted':
        setMedia(prev => prev.filter(m => m.id !== message.data.id));
        return;
        
      case 'playlist_deleted':
        setPlaylists(prev => prev.filter(p => p.id !== message.data.id));
        return;
    }
    
    // Debounce reload für alle anderen Events (created, updated, etc.)
    if (loadDataTimeoutRef.current) {
      clearTimeout(loadDataTimeoutRef.current);
    }
    
    loadDataTimeoutRef.current = setTimeout(() => {
      console.log('🔄 Reloading data after WebSocket event:', message.type);
      loadDataInternal();
    }, 300);
  }, [loadDataInternal, addToast, t]);

  // WebSocket mit korrekter Organization ID
  const wsOrgId = viewingAsOrganization?.id || currentUser?.organization_id;
  useWebSocket('dashboard', wsOrgId, handleWebSocketMessage);

  // ============================================
  // SUPER ADMIN FUNCTIONS
  // ============================================
  const loadAllOrganizations = useCallback(async () => {
    try {
      const savedToken = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/organizations`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load organizations');
      }

      const data = await response.json();
      console.log('🏢 Loaded all organizations:', data);

      setAllOrganizations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading organizations:', error);
      setAllOrganizations([]);
    }
  }, []);

  const switchToOrganization = useCallback((org) => {
    console.log('🔄 Switching to organization:', org.name, 'ID:', org.id);

    // Speichere in localStorage
    localStorage.setItem('viewingAsOrgId', org.id.toString());
    console.log('   Saved to localStorage:', localStorage.getItem('viewingAsOrgId'));

    // State setzen
    setViewingAsOrganization(org);
    setShowOrgSwitcher(false);

    // loadData wird automatisch durch useEffect getriggert
    console.log('   ✓ loadData will be triggered by useEffect...');
  }, []);

  const switchBackToOwnOrganization = useCallback(() => {
    console.log('🔙 Switching back to own organization');

    // localStorage löschen
    localStorage.removeItem('viewingAsOrgId');
    console.log('   Removed from localStorage');

    // State zurücksetzen
    setViewingAsOrganization(null);

    // loadData wird automatisch durch useEffect getriggert
    console.log('   ✓ loadData will be triggered by useEffect...');
  }, []);

  // NEU: Direkter Aufruf ohne Debounce beim Mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);

      const userStr = localStorage.getItem('user');
      if (userStr) {
        setCurrentUser(JSON.parse(userStr));
      }

      loadDataInternal();  // ⬅ Direkter Aufruf ohne Debounce!
    }
  }, []); // ⬅ Leere Dependencies, läuft nur beim Mount

  // ============================================
  // LOAD ORGANIZATIONS FOR SUPER ADMIN
  // ============================================
  useEffect(() => {
    if (currentUser?.role === 'super_admin') {
      loadAllOrganizations();
    }
  }, [currentUser, loadAllOrganizations]);

  // Load organizations when user is super_admin
  useEffect(() => {
    if (currentUser?.role === 'super_admin') {
      loadAllOrganizations();
    }
  }, [currentUser, loadAllOrganizations]);

  // NEU: Reload data when viewing organization changes
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔄 viewingAsOrganization changed, reloading data...');
      loadData();
    }
  }, [viewingAsOrganization, isAuthenticated, loadData]);

  // ============================================
  // AUTH FUNCTIONS
  // ============================================
  const handleLogin = async () => {
    setLoginError('');
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('userRole', data.user.role);
        localStorage.setItem('subscriptionTier', data.user.subscriptionTier || 'starter'); // NEU
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('user', JSON.stringify(data.user));

        setToken(data.token);
        setCurrentUser(data.user);
        setIsAuthenticated(true);
        loadData();
      } else {
        setLoginError(data.error || t('dashboard.errors.loginFailed'));
      }
    } catch (error) {
      setLoginError(t('dashboard.errors.connectionError'));
    }
  };

  const handleLogout = () => {
  // Smooth fade-out
  document.body.style.opacity = '0';
  document.body.style.transition = 'opacity 0.2s ease';
  
  setTimeout(() => {
    // ✅ Verwende authUtils zum Löschen
    authUtils.clearAuth();
    
    // ✅ Reset nur die States die du WIRKLICH hast
    setToken(null);
    setIsAuthenticated(false);
    setScreens([]);
    setMedia([]);
    setUsers([]);
    setPlaylists([]);
    setGroups([]);
    setActiveGroup(null);
    
    // Falls du viewingAsOrganization hast (weiter unten definiert)
    if (typeof setViewingAsOrganization !== 'undefined') {
      setViewingAsOrganization(null);
    }
    
    navigate('/login');
    
    // Reset opacity
    setTimeout(() => {
      document.body.style.opacity = '1';
    }, 50);
  }, 200);
};

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setNewMedia({ ...newMedia, name: file.name });
    }
  };

  const addMedia = async () => {
    const savedToken = localStorage.getItem('token');

    if (uploadMethod === 'file' && selectedFile) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('duration', newMedia.duration);

        const response = await fetch(`${API_URL}/media/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${savedToken}` },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Upload failed');
        }

        setNewMedia({ name: '', type: 'image', url: '', duration: 10 });
        setSelectedFile(null);
        setShowAddMediaModal(false);
        loadData();
      } catch (error) {
        alert(t('dashboard.errors.uploadError', { message: error.message }));
      } finally {
        setUploading(false);
      }
    } else if (uploadMethod === 'url' && newMedia.name && newMedia.url) {
      try {
        const urlPattern = /^https?:\/\/.+/i;
        if (!urlPattern.test(newMedia.url)) {
          alert(t('dashboard.errors.invalidUrl'));
          return;
        }

        if (newMedia.name.length < 2) {
          alert(t('dashboard.errors.nameTooShort'));
          return;
        }

        const response = await fetch(`${API_URL}/media`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${savedToken}`
          },
          body: JSON.stringify(newMedia)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || t('dashboard.errors.createMediaFailed'));
        }

        setNewMedia({ name: '', type: 'image', url: '', duration: 10 });
        setShowAddMediaModal(false);
        loadData();
      } catch (error) {
        alert('Error: ' + error.message);
      }
    }
  };

  const deleteMedia = async (id) => {
    try {
      const savedToken = localStorage.getItem('token');
      await fetch(`${API_URL}/media/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${savedToken}` }
      });
      setMedia(media.filter(m => m.id !== id));
    } catch (error) {
      alert(t('dashboard.errors.deleteMediaFailed'));
    }
  };

  const deleteScreen = async (id) => {
    try {
      const savedToken = localStorage.getItem('token');
      await fetch(`${API_URL}/screens/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${savedToken}` }
      });
      setScreens(screens.filter(s => s.id !== id));
    } catch (error) {
      alert(t('dashboard.errors.deleteScreenFailed'));
    }
  };

  const assignPlaylist = async (screenId, playlistId) => {
    try {
      const savedToken = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/screens/${screenId}/playlist`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${savedToken}`
        },
        body: JSON.stringify({ playlist_id: playlistId ? parseInt(playlistId) : null })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to assign playlist');
      }
    } catch (error) {
      alert('Error assigning playlist: ' + error.message);
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    if (!loginEmail) {
      setLoginError('Bitte geben Sie Ihre Email-Adresse ein');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail })
      });

      if (response.ok) {
        setLoginError('');
        alert(t('dashboard.errors.verificationEmailSent'));
      } else {
        const data = await response.json();
        setLoginError(data.error || t('dashboard.errors.sendEmailFailed'));
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setLoginError(t('dashboard.errors.networkError'));
    }
  };

  // ============================================
  // EDIT SCREEN MODAL FUNCTIONS
  // ============================================
  const openEditScreen = (screen) => {
  setEditingScreen(screen);
};

  const handleSaveScreen = async (screenId, formData) => {
  try {
    const savedToken = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/screens/${screenId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${savedToken}`
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update screen');
    }

    loadDataInternal(); // Reload data
  } catch (error) {
    console.error('Error updating screen:', error);
    throw error; // Modal will catch this and show error
  }
};

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  const getTimeSinceLastSeen = (lastHeartbeat) => {
    if (!lastHeartbeat) return t('dashboard.timeAgo.neverOnline');
    
    const now = new Date();
    const last = new Date(lastHeartbeat);
    const diffMs = now - last;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    
    if (diffSec < 60) return t('dashboard.timeAgo.fewSeconds');
    if (diffMin < 60) return t('dashboard.timeAgo.minutes', { count: diffMin });
    if (diffHour < 24) return t('dashboard.timeAgo.hours', { count: diffHour });
    return t('dashboard.timeAgo.days', { count: Math.floor(diffHour / 24) });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        
        {/* NEU: CSS Styles für kompakte Cards */}
        <style>{`
          .screen-card-compact {
            transition: all 0.2s ease;
          }
          
          .screen-card-compact:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            transform: translateY(-2px);
          }
          
          .status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            flex-shrink: 0;
          }
          
          .status-dot.online {
            background: #10b981;
            animation: pulse-dot 2s infinite;
          }
          
          .status-dot.offline {
            background: #ef4444;
          }
          
          @keyframes pulse-dot {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          .action-btn {
  padding: 0.625rem; /* 10px - größer auf Mobile */
  color: #9ca3af;
  transition: all 0.2s ease;
  border-radius: 0.5rem;
  min-width: 40px; /* Minimum touch target */
  min-height: 40px; /* Minimum touch target */
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Mobile: Noch größere Touch Targets */
@media (max-width: 768px) {
  .action-btn {
    min-width: 44px; /* Apple HIG minimum */
    min-height: 44px;
    padding: 0.75rem;
  }
}
          
          .action-btn:hover {
            transform: translateY(-1px);
          }
          
          .action-btn.play:hover {
            color: #2563eb;
            background-color: #eff6ff;
          }
          
          .action-btn.qr:hover {
            color: #9333ea;
            background-color: #faf5ff;
          }
          
          .action-btn.edit:hover {
            color: #6b7280;
            background-color: #f3f4f6;
          }
          
          .action-btn.delete:hover {
            color: #ef4444;
            background-color: #fef2f2;
          }

          /* Better tap feedback on mobile */
.screen-card-compact {
  transition: all 0.2s ease;
  position: relative;
}

.screen-card-compact:active {
  transform: scale(0.98);
}

/* Tap highlight color (iOS) */
.action-btn {
  -webkit-tap-highlight-color: rgba(43, 127, 215, 0.1);
}

/* Active state for better feedback */
.action-btn:active {
  transform: scale(0.9);
  opacity: 0.8;
}
        `}</style>

        <style>{`
  /* Mobile Header Optimizations */
  @media (max-width: 768px) {
    /* Reduce header height */
    header {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
    
    /* Compact organization info */
    .org-info {
      font-size: 0.875rem;
    }
    
    /* Hide less important text */
    .hide-mobile-text {
      display: none;
    }
    
    /* Sticky header offset */
    main {
      padding-top: 1rem;
    }
  }
  
  /* Smooth transitions */
  header, nav {
    transition: all 0.2s ease;
  }
  
  /* Mobile safe area (iPhone notch) */
  @supports(padding: max(0px)) {
    header {
      padding-left: max(1rem, env(safe-area-inset-left));
      padding-right: max(1rem, env(safe-area-inset-right));
    }
  }

  /* iPhone Safe Area (Bottom Navigation Bar) */
@supports(padding: max(0px)) {
  main {
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  
  /* Floating buttons */
  .floating-action-button {
    bottom: max(1rem, calc(env(safe-area-inset-bottom) + 1rem));
  }
}

/* Prevent content from going under mobile nav */
@media (max-width: 768px) {
  main {
    padding-bottom: 120px; /* Erhöht von 80px auf 120px */
  }
  
  /* Extra spacing for last screen card */
  .screen-cards-container > div:last-child {
    margin-bottom: 30px;
  }
}

/* Prevent overscroll bounce that looks like bug */
body {
  overscroll-behavior-y: contain;
}

/* Smooth scroll */
html {
  scroll-behavior: smooth;
}

/* ========================================
   MOBILE MODAL OPTIMIZATIONS
   ======================================== */

/* Full-screen modals on mobile */
@media (max-width: 768px) {
  /* Modal backdrop */
  .modal-backdrop {
    padding: 0 !important;
  }
  
  /* Modal container */
  .modal-container {
    max-width: 100% !important;
    min-height: 100vh;
    margin: 0 !important;
    border-radius: 0 !important;
    display: flex;
    flex-direction: column;
  }
  
  /* Modal content scrollable */
  .modal-content {
    flex: 1;
    overflow-y: auto;
    padding-bottom: max(2rem, env(safe-area-inset-bottom));
  }
  
  /* Modal header sticky */
  .modal-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    padding: 1rem;
  }
  
  /* Dark mode */
  .dark .modal-header {
    background: #1f2937;
    border-bottom-color: #374151;
  }
  
  /* Modal footer sticky */
  .modal-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: white;
    border-top: 1px solid #e5e7eb;
    padding: 1rem;
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  
  .dark .modal-footer {
    background: #1f2937;
    border-top-color: #374151;
  }
}

/* ========================================
   FORM OPTIMIZATIONS
   ======================================== */

/* Larger input fields on mobile */
@media (max-width: 768px) {
  input[type="text"],
  input[type="email"],
  input[type="password"],
  input[type="number"],
  input[type="url"],
  select,
  textarea {
    font-size: 16px !important; /* Prevent zoom on iOS */
    min-height: 48px;
    padding: 0.75rem 1rem !important;
  }
  
  /* Labels larger */
  label {
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  /* Buttons larger */
  button {
    min-height: 48px;
    font-size: 1rem;
    font-weight: 600;
  }
}

/* Touch-friendly spacing */
.form-group {
  margin-bottom: 1.5rem;
}

@media (max-width: 768px) {
  .form-group {
    margin-bottom: 1.25rem;
  }
}

/* ========================================
   BOTTOM NAVIGATION BAR - TAG MOBILE-4
   ======================================== */

/* Safe area support for iPhone notch/home indicator */
@supports (padding: max(0px)) {
  .safe-area-bottom {
    padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
  }
  
  .pb-safe {
    padding-bottom: env(safe-area-inset-bottom);
  }
}

/* Add bottom padding to content when bottom nav is visible */
@media (max-width: 768px) {
  .dashboard-content {
    padding-bottom: calc(4rem + env(safe-area-inset-bottom, 0px));
  }
}
  
`}</style>

        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md w-full max-w-md border border-gray-200 dark:border-gray-700 transition-colors">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <Monitor className="text-primary-600 dark:text-primary-400" size={40} />
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">AlpenScreen</h1>
          </div>

          <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
            Willkommen zurück! Bitte melden Sie sich an.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('auth.email')}</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                placeholder="ihre.email@firma.ch"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('auth.password')}</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                placeholder="••••••••"
              />
            </div>
            
            {loginError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded text-sm">
                {loginError}
                
                {/* Show resend verification link if email not verified */}
                {loginError.includes('Email') && loginError.includes('bestätigen') && (
                  <div className="mt-2 pt-2 border-t border-red-300 dark:border-red-700">
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleResendVerification();
                      }}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
                    >
                      Bestätigungs-Email erneut senden
                    </a>
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={handleLogin}
              className="w-full bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2 rounded transition-colors font-semibold"
            >
              {t('auth.login')}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Noch kein Account?
                </span>
              </div>
            </div>

            {/* Register Link */}
            <a 
              href="/register.html"
              className="block w-full text-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded transition-colors font-medium"
            >
              Jetzt kostenlos registrieren →
            </a>

            {/* Forgot password link (for later) */}
            <div className="text-center mt-4">
              <a 
                href="/forgot-password.html" 
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Passwort vergessen?
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const DashboardView = ({ trialInfo }) => {
    const userRole = localStorage.getItem('userRole');
    const [copiedId, setCopiedId] = useState(null);

    // Nur filtern, Status kommt direkt aus screens
    const filteredScreens = activeGroup
      ? (screens || []).filter(s => s.group_id === activeGroup.id)
      : (screens || []);

    // DEBUG (optional, kannst du auch löschen)
    console.log('🖥️ FilteredScreens:', filteredScreens.map(s => ({
      name: s.name,
      status: s.status,
      last_seen: s.last_seen
    })));

    const copyPlayerUrl = (uuid, screenId) => {
      const fullUrl = `${window.location.origin}/player/${uuid}`;
      navigator.clipboard.writeText(fullUrl).then(() => {
        setCopiedId(screenId);
        setTimeout(() => setCopiedId(null), 2000);
      });
    };

    const [editingScreen, setEditingScreen] = useState(null);

    const getScreenOwnerInfo = (screen) => {
      // Wenn der Screen vom aktuellen User ist
      const currentUserId = parseInt(localStorage.getItem('userId') || '0');
      if (screen.created_by === currentUserId) {
        return { label: 'You', color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900', textColor: 'text-blue-700 dark:text-blue-300' };
      }

      // Für Admins: Zeige den User-Namen wenn verfügbar
      if (userRole === 'admin') {
        const owner = users.find(u => u.id === screen.created_by);
        if (owner) {
          return { label: owner.name || owner.email.split('@')[0], color: 'purple', bgColor: 'bg-purple-100 dark:bg-purple-900', textColor: 'text-purple-700 dark:text-purple-300' };
        }
      }

      return { label: 'Other User', color: 'gray', bgColor: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-700 dark:text-gray-300' };
    };

    const assignGroupToScreen = async (screenId, groupId) => {
      try {
        const response = await fetch(`${API_URL}/groups/assign/${screenId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ group_id: groupId || null })
        });

        if (response.ok) {
          loadData();
        } else {
          alert('Failed to assign group');
        }
      } catch (error) {
        console.error('Error assigning group:', error);
        alert('Error assigning group');
      }
    };

    return (
      <div className="flex h-full">
        {/* Group Sidebar - versteckt auf Mobile */}
        <div className="hidden lg:block">
          <GroupSidebar
            groups={groups}
            activeGroup={activeGroup}
            onSelectGroup={setActiveGroup}
            totalScreens={(screens || []).length}
            token={token}
            loadData={loadData}
            setActiveGroup={setActiveGroup}
            setEditingGroup={setEditingGroup}
            setShowEditGroupModal={setShowEditGroupModal}
            setShowCreateGroupModal={setShowCreateGroupModal}
          />
        </div> {/* Ende Sidebar Container */}

        {/* Main Content */}
<div className="flex-1 overflow-y-auto p-4 md:p-6">

  {/* Tier Badge - Shows current plan, screen usage, and upgrade options */}
{currentUser?.role !== 'super_admin' && (
  <div className="mb-4 md:mb-6">
    <TierBadge />
  </div>
)}
  
          <div className="space-y-4 md:space-y-6">
            <ScreenHealthMonitor screens={filteredScreens} />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100">
                  {activeGroup ? (
                    <span className="flex items-center gap-2">
                      <span>{activeGroup.icon}</span>
                      <span>{activeGroup.name}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({filteredScreens.length} {t('dashboard.screens', { count: filteredScreens.length })})
                      </span>
                    </span>
                  ) : (
                    t('dashboard.allScreens')
                  )}
                </h2>
                {activeGroup && activeGroup.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {activeGroup.description}
                  </p>
                )}
              </div>
              <button
  onClick={() => setShowAddScreenModal(true)}
  className="flex items-center gap-2 bg-primary-600 text-white px-4 py-3 md:py-2 rounded-lg hover:bg-primary-700 active:scale-95 transition-all text-sm md:text-base w-full md:w-auto justify-center shadow-lg md:shadow-md font-semibold"
>
  <Plus size={18} className="md:w-5 md:h-5" /> 
  <span className="font-medium">{t('dashboard.addScreen')}</span>
</button>
            </div>

            {filteredScreens.length === 0 ? (
              <div className="text-center py-12">
                <Monitor className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base">
                  {activeGroup ? `${t('dashboard.noScreensInGroup')} ${activeGroup.name}` : t('dashboard.noScreens')}
                </p>
              </div>
            ) : (
              <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-1 md:gap-4">
                
{filteredScreens.map(screen => {
  const isOnline = screen.status === 'online';
  const timeSince = getTimeSinceLastSeen(screen.last_seen);

  return (
    <ScreenCard
      key={screen.id}
      screen={screen}
      isOnline={isOnline}
      timeSince={timeSince}
      onOpenPlayer={(uuid) => window.open(`/player/${uuid}`, '_blank')}
      onEdit={openEditScreen}
      onShowQR={(screen) => {
        setSelectedScreenForQR(screen);
        setShowQRModal(true);
      }}
      onDelete={deleteScreen}
      groups={groups}
      playlists={playlists}
      t={t}
    />
  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const UsersView = () => {
    const { t } = useTranslation(); // ⭐ WICHTIG: useTranslation Hook hinzufügen
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOrg, setFilterOrg] = useState('all');
    const [filterRole, setFilterRole] = useState('all');

    // Calculate stats
    const totalUsers = users.length;
    const adminCount = users.filter(u => u.role === 'admin' || u.role === 'super_admin').length;
    const activeCount = users.filter(u => u.status === 'active' || !u.status).length;
    const orgCount = allOrganizations.length;

    // Filter users
    const filteredUsers = users.filter(user => {
      const matchesSearch = 
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesOrg = filterOrg === 'all' || user.organization_id === parseInt(filterOrg);
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      
      return matchesSearch && matchesOrg && matchesRole;
    });

    // Group users by organization
    const usersByOrg = {};
    filteredUsers.forEach(user => {
      const orgId = user.organization_id;
      if (!usersByOrg[orgId]) {
        usersByOrg[orgId] = [];
      }
      usersByOrg[orgId].push(user);
    });

    // Get organization info
const getOrgInfo = (orgId) => {
  // Debug logging
  console.log('getOrgInfo called with orgId:', orgId);
  console.log('allOrganizations:', allOrganizations);
  
  if (currentUser?.role === 'super_admin') {
    const foundOrg = allOrganizations.find(o => o.id === orgId);
    console.log('Found org:', foundOrg);
    
    if (foundOrg) {
      return {
        id: foundOrg.id,
        name: foundOrg.name || 'Unknown',
        color: foundOrg.color || '#2563eb',
        logo_url: foundOrg.logo_url || null,
        subscription_status: foundOrg.subscription_status,
        subscription_tier: foundOrg.subscription_tier
      };
    }
    
    // Fallback wenn nicht gefunden
    return { 
      id: orgId,
      name: 'Unknown Organization', 
      color: '#2563eb',
      logo_url: null 
    };
  }
  
  // Für normale Admins
  return {
    id: currentUser?.organization?.id,
    name: currentUser?.organization?.name || 'Unknown',
    color: currentUser?.organization?.color || '#2563eb',
    logo_url: currentUser?.organization?.logo_url || null
  };
};

    // Get initials from email
    const getInitials = (email, name) => {
      if (name) {
        const parts = name.split(' ');
        return parts.length > 1 
          ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
          : parts[0].substring(0, 2).toUpperCase();
      }
      return email.substring(0, 2).toUpperCase();
    };

    // Random color for avatar
    const getAvatarColor = (email) => {
      const colors = [
        'bg-blue-100 text-primary-600',
        'bg-purple-100 text-purple-600',
        'bg-green-100 text-green-600',
        'bg-orange-100 text-orange-600',
        'bg-pink-100 text-pink-600',
        'bg-indigo-100 text-indigo-600',
      ];
      const index = email.charCodeAt(0) % colors.length;
      return colors[index];
    };

    const handleChangeRole = async (userId, currentRole) => {
      const newRole = currentRole === 'admin' ? 'user' : 'admin';
      if (window.confirm(t('users.confirmChangeRole', { role: newRole }))) {
        try {
          const savedToken = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/users/${userId}/role`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${savedToken}`
            },
            body: JSON.stringify({ role: newRole })
          });
          if (response.ok) {
            loadData();
          } else {
            const error = await response.json();
            alert(error.error || t('users.errors.updateRoleFailed'));
          }
        } catch (error) {
          alert(t('users.errors.updateRoleFailed'));
        }
      }
    };

    const handleDeleteUser = async (userId) => {
      if (window.confirm(t('users.confirmDelete'))) {
        try {
          const savedToken = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/users/${userId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          if (response.ok) {
            loadData();
          } else {
            const error = await response.json();
            alert(error.error || t('users.errors.deleteFailed'));
          }
        } catch (error) {
          alert(t('users.errors.deleteFailed'));
        }
      }
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
            <Users size={32} className="text-primary-600 dark:text-primary-400" />
            {t('users.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {currentUser?.role === 'super_admin' 
              ? t('users.subtitle')
              : t('users.subtitleOrgAdmin')}
          </p>

        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          {/* Total Users */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 md:p-6 border-l-4 border-primary-500">
            <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-2">
              <div>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-1">{t('users.totalUsers')}</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">{totalUsers}</p>
              </div>
              <div className="w-8 h-8 md:w-12 md:h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Users className="text-primary-600 dark:text-primary-400" size={16} />
              </div>
            </div>
          </div>

          {/* Admins */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 md:p-6 border-l-4 border-purple-500">
            <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-2">
              <div>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-1">{t('users.admins')}</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">{adminCount}</p>
              </div>
              <div className="w-8 h-8 md:w-12 md:h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 md:w-6 md:h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Active Users */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 md:p-6 border-l-4 border-green-500">
            <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-2">
              <div>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-1">{t('users.active')}</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">{activeCount}</p>
              </div>
              <div className="w-8 h-8 md:w-12 md:h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 md:w-6 md:h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Organizations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 md:p-6 border-l-4 border-orange-500">
            <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-2">
              <div>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-1">{t('users.orgs')}</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {currentUser?.role === 'super_admin' ? orgCount : 1}
                </p>
              </div>
              <div className="w-8 h-8 md:w-12 md:h-12 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <Building2 className="text-orange-600 dark:text-orange-400" size={16} />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 md:p-4">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder={t('users.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 md:pl-10 pr-4 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Filter by Organization - Only for Super Admin */}
            {currentUser?.role === 'super_admin' && (
              <div className="w-full md:w-64">
                <select
                  value={filterOrg}
                  onChange={(e) => setFilterOrg(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">{t('users.allOrganizations')}</option>
                  {allOrganizations.map(org => (
                    <option key={org.id} value={org.id}>
                      🏢 {org.name} ({users.filter(u => u.organization_id === org.id).length})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Filter by Role */}
            <div className="w-full md:w-48">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('users.allRoles')}</option>
                {currentUser?.role === 'super_admin' && <option value="super_admin">👑 {t('users.superAdmin')}</option>}
                <option value="admin">🔒 {t('users.admin')}</option>
                <option value="user">👤 {t('users.user')}</option>
              </select>
            </div>

            {/* Add User Button */}
            <button
              onClick={() => setShowAddUser(true)}
              className="px-4 md:px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 w-full md:w-auto text-sm md:text-base"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">{t('users.addUser')}</span>
              <span className="sm:hidden">{t('common.add')}</span>
            </button>
          </div>
        </div>

        {/* Organization Cards */}
        <div className="space-y-6">
          {Object.keys(usersByOrg).length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
              <Users className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                {t('users.noUsersFound')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {searchTerm || filterOrg !== 'all' || filterRole !== 'all'
                  ? t('users.tryAdjustingFilters')
                  : t('users.createFirstUser')}
              </p>
            </div>
          ) : (
            Object.keys(usersByOrg).map(orgId => {
              const org = getOrgInfo(parseInt(orgId));
              const orgUsers = usersByOrg[orgId];

              return (
                <div key={orgId} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden transition-all hover:shadow-lg">
                  {/* Organization Header */}
<div 
  className="px-6 py-4 flex items-center justify-between relative"
  style={{
    background: `linear-gradient(135deg, ${org.color} 0%, ${org.color}dd 50%, ${org.color}bb 100%)`
  }}
>
  {/* Dark overlay for text contrast */}
  <div className="absolute inset-0 bg-black/25"></div>
  
  {/* Content */}
  <div className="relative z-10 flex items-center gap-3">
    <div 
      className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-xl font-bold shadow-lg"
      style={{ color: org.color }}
    >
      {org.logo_url ? (
        <img
          src={`${API_BASE_URL}${org.logo_url}`}
          alt={org.name}
          className="w-12 h-12 rounded-lg object-cover"
        />
      ) : (
        org.name.charAt(0).toUpperCase()
      )}
    </div>
    <div>
      <h2 className="text-xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
        {org.name}
      </h2>
      <p className="text-white/95 text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
        {t('users.usersCount', { count: orgUsers.length })} • ID: {orgId}
      </p>
    </div>
  </div>
  
  <div className="relative z-10 flex items-center gap-2">
    <span className="px-3 py-1 bg-white/30 backdrop-blur-sm text-white text-sm rounded-full font-semibold border border-white/40 shadow-sm">
      Aktiv
    </span>
    {currentUser?.role === 'super_admin' && (
      <button
        onClick={() => switchToOrganization(org)}
        className="px-4 py-2 bg-white hover:bg-white/95 rounded-lg transition-all text-sm font-semibold shadow-lg hover:shadow-xl"
        style={{ color: org.color }}
      >
        {t('users.viewOrganization')}
      </button>
    )}
  </div>
</div>

                  {/* Users List */}
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {orgUsers.map(user => (
                      <div
                        key={user.id}
                        className="px-6 py-4 flex items-center justify-between hover:bg-blue-50/50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${getAvatarColor(user.email)}`}>
                            {getInitials(user.email, user.name)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                {user.email}
                              </h3>
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                user.role === 'admin' || user.role === 'super_admin'
                                  ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                              }`}>
                                {user.role === 'super_admin' 
                                  ? `👑 ${t('users.superAdmin')}` 
                                  : user.role === 'admin' 
                                  ? `🔒 ${t('users.admin')}` 
                                  : `👤 ${t('users.user')}`}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {user.name || t('users.noName')} • {t('users.created')}: {new Date(user.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleChangeRole(user.id, user.role)}
                            disabled={user.role === 'super_admin'}
                            className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('users.changeRole')}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.role === 'super_admin'}
                            className="px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Trash2 size={16} />
                            {t('users.delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const QRCodeModal = ({ screen, onClose }) => {
  const { t } = useTranslation(); // Add this import at top: import { useTranslation } from 'react-i18next';
  
  if (!screen) return null;

  const playerUrl = `${window.location.origin}/player/${screen.uuid}`;

  const handlePrint = () => {
    window.print();
  };

  const downloadQR = () => {
    const canvas = document.getElementById('qr-code-canvas');
    const svg = canvas.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qr-${screen.name.replace(/\s+/g, '-')}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 md:p-4 modal-backdrop">
      <div className="bg-white dark:bg-gray-800 rounded-lg md:rounded-lg p-0 md:p-8 max-w-md w-full transition-colors modal-container max-h-screen overflow-y-auto">
        
        {/* Header - sticky on mobile */}
        <div className="modal-header md:mb-6 md:pt-0">
          <div className="flex items-center justify-between">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('qrCode.title')}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="modal-content px-4 md:px-0">
          {/* Screen Name Badge */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Monitor className="text-primary-600 dark:text-primary-400" size={20} />
              <span className="font-semibold text-blue-900 dark:text-blue-100">
                {screen.name}
              </span>
            </div>
          </div>

          {/* QR Code */}
          <div id="qr-code-canvas" className="bg-white p-6 md:p-8 rounded-lg flex justify-center mb-6">
            <QRCodeSVG
              value={playerUrl}
              size={window.innerWidth < 768 ? 200 : 256}
              level="H"
              includeMargin={true}
            />
          </div>

          {/* Player URL */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 text-center font-semibold">
              {t('qrCode.playerUrl')}
            </p>
            <p className="text-xs font-mono text-gray-900 dark:text-gray-100 break-all text-center">
              {playerUrl}
            </p>
          </div>

          {/* Setup Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {t('qrCode.setupInstructions.title')}
            </h4>
            <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-2 list-decimal list-inside">
              <li>{t('qrCode.setupInstructions.step1')}</li>
              <li>{t('qrCode.setupInstructions.step2')}</li>
              <li>{t('qrCode.setupInstructions.step3')}</li>
              <li>{t('qrCode.setupInstructions.step4')}</li>
              <li>{t('qrCode.setupInstructions.step5')}</li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              onClick={downloadQR}
              className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-3 rounded-lg transition-colors font-semibold text-sm md:text-base"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="hidden sm:inline">{t('qrCode.buttons.download')}</span>
              <span className="sm:hidden">{t('qrCode.buttons.downloadShort')}</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white px-4 py-3 rounded-lg transition-colors font-semibold text-sm md:text-base"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {t('qrCode.buttons.print')}
            </button>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-3 rounded-lg transition-colors font-semibold"
          >
            {t('qrCode.buttons.close')}
          </button>
        </div>
        
      </div>
    </div>
  );
};

  // ==================== GROUP COMPONENTS ====================

  const GroupModal = ({ group, token, onClose, onSave, loadData }) => {
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
    color: group?.color || '#2563eb',
    icon: group?.icon || 'building2'
  });

  const colorOptions = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Check if viewing as different organization
      const viewingOrgId = localStorage.getItem('viewingAsOrgId');
      const userRole = localStorage.getItem('userRole');

      let url;
      if (group) {
        // Update existing group
        url = `${API_URL}/groups/${group.id}`;
        if (viewingOrgId && userRole === 'super_admin') {
          url += `?filter_org=${viewingOrgId}`;
        }
      } else {
        // Create new group
        url = `${API_URL}/groups`;
        if (viewingOrgId && userRole === 'super_admin') {
          url += `?filter_org=${viewingOrgId}`;
        }
      }

      const method = group ? 'PUT' : 'POST';

      console.log('🏗 Group operation:', method);
      console.log('   📡 URL:', url);
      console.log('   📦 Data:', formData);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const savedGroup = await response.json();
        console.log('Group saved:', savedGroup);

        // Close modal
        onClose();

        // Call callback
        if (onSave) {
          onSave(savedGroup);
        }

        // Reload data after short delay
        setTimeout(() => {
          console.log('🔄 Reloading data after group operation...');
          if (loadData) {
            loadData();
          }
        }, 200);
      } else {
        const error = await response.json();
        console.error('❌ Failed to save group:', error);
        alert('Failed to save group: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error saving group:', error);
      alert('Error saving group: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 md:p-0 modal-backdrop">
      <div className="bg-white dark:bg-gray-800 rounded-lg md:rounded-lg p-0 md:p-6 w-full max-w-md transition-colors modal-container">
        
        {/* Header - sticky on mobile */}
        <div className="modal-header md:mb-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {group ? t('groupModal.editGroup') : t('groupModal.createNewGroup')}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="modal-content px-4 md:px-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                {t('groupModal.groupNameRequired')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                placeholder={t('groupModal.groupNamePlaceholder')}
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                {t('groupModal.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors resize-none"
                placeholder={t('groupModal.descriptionPlaceholder')}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                {t('groupModal.icon')}
              </label>
              <div className="grid grid-cols-5 gap-2 md:gap-3">
                {iconOptions.map(({ id, component: IconComponent, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: id })}
                    className={`p-3 md:p-3 rounded-lg border-2 transition-all flex items-center justify-center ${
                      formData.icon === id
                        ? 'border-primary-500 bg-blue-50 dark:bg-blue-900 scale-110'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                    title={label}
                  >
                    <IconComponent 
                      size={24} 
                      className="text-gray-700 dark:text-gray-300"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                {t('groupModal.color')}
              </label>
              <div className="grid grid-cols-4 gap-2 md:gap-3">
                {colorOptions.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`h-12 md:h-10 rounded-lg border-2 transition-all ${
                      formData.color === color
                        ? 'border-gray-800 dark:border-white scale-110'
                        : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                {t('groupModal.preview')}
              </p>
              <div
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
                style={{
                  backgroundColor: `${formData.color}20`,
                  borderLeft: `4px solid ${formData.color}`
                }}
              >
                {renderGroupIcon(formData.icon, 20, '')}
                <span style={{ color: formData.color }}>
                  {formData.name || t('groupModal.groupNameFallback')}
                </span>
              </div>
            </div>

            {/* Footer - sticky on mobile */}
            <div className="modal-footer md:pt-2 -mx-4 md:mx-0">
              <div className="flex flex-col-reverse md:flex-row gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-3 md:py-2 rounded-lg transition-colors font-semibold"
                >
                  {t('groupModal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!formData.name.trim()}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-3 md:py-2 rounded-lg transition-colors font-semibold"
                >
                  {group ? t('groupModal.updateGroup') : t('groupModal.createGroup')}
                </button>
              </div>
            </div>
          </form>
        </div>
        
      </div>
    </div>
  );
};

  const GroupSidebar = ({ groups, activeGroup, onSelectGroup, totalScreens }) => {
    const [hoveredGroup, setHoveredGroup] = useState(null);
    const [draggedGroup, setDraggedGroup] = useState(null);
    const [dragOverGroup, setDragOverGroup] = useState(null);

    const handleDeleteGroup = async (groupId, e) => {
      e.stopPropagation();

      // ✅ GEÄNDERT: Übersetzt
      const message = `${t('sidebar.confirmDelete.title')}\n${t('sidebar.confirmDelete.message')}`;
      if (!window.confirm(message)) {
        return;
      }

      try {
        const savedToken = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/groups/${groupId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${savedToken}`
          }
        });

        if (response.ok) {
          loadData();
          if (activeGroup?.id === groupId) {
            onSelectGroup(null);
          }
        } else {
          // ✅ GEÄNDERT: Übersetzt
          alert(t('sidebar.errors.deleteFailed'));
        }
      } catch (error) {
        console.error('Error deleting group:', error);
        // ✅ GEÄNDERT: Übersetzt
        alert(t('sidebar.errors.deleteError'));
      }
    };

    const handleEditGroup = (group, e) => {
      e.stopPropagation();
      setEditingGroup(group);
      setShowEditGroupModal(true);
    };

    const handleDragStart = (group, e) => {
      console.log('🖱 Drag started:', group.name);
      setDraggedGroup(group);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', group.id);
    };

    const handleDragOver = (group, e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverGroup !== group.id) {
        setDragOverGroup(group.id);
      }
    };

    const handleDragLeave = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;

      if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
        setDragOverGroup(null);
      }
    };

    const handleDragEnd = () => {
      console.log('🏁 Drag ended');
      setDraggedGroup(null);
      setDragOverGroup(null);
    };

    const handleDrop = async (targetGroup, e) => {
      e.preventDefault();
      e.stopPropagation();

      console.log('📥 Drop event triggered');
      console.log('   From:', draggedGroup?.name);
      console.log('   To:', targetGroup.name);

      setDragOverGroup(null);

      if (!draggedGroup) {
        console.log('❌ No dragged group');
        return;
      }

      if (draggedGroup.id === targetGroup.id) {
        console.log('❌ Same group, ignoring');
        setDraggedGroup(null);
        return;
      }

      // Reorder logic
      const reorderedGroups = [...groups];
      const draggedIndex = reorderedGroups.findIndex(g => g.id === draggedGroup.id);
      const targetIndex = reorderedGroups.findIndex(g => g.id === targetGroup.id);

      console.log('📊 Indices:', { draggedIndex, targetIndex });

      if (draggedIndex === -1 || targetIndex === -1) {
        console.log('❌ Invalid indices');
        setDraggedGroup(null);
        return;
      }

      // Remove dragged item and insert at target position
      const [removed] = reorderedGroups.splice(draggedIndex, 1);
      reorderedGroups.splice(targetIndex, 0, removed);

      // Update sort_order for all groups
      const groupOrders = reorderedGroups.map((group, index) => ({
        id: group.id,
        sort_order: index
      }));

      console.log('📋 New order:', groupOrders);

      try {
        const savedToken = localStorage.getItem('token');
        console.log('📡 Sending reorder request...');

        const response = await fetch(`${API_URL}/groups/reorder`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${savedToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ groupOrders })
        });

        console.log('📡 Response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Reorder successful:', data);
          loadData();
        } else {
          const error = await response.json();
          console.error('❌ Reorder failed:', error);
          // ✅ GEÄNDERT: Übersetzt
          alert(t('sidebar.errors.reorderFailed', { error: error.error || 'Unknown error' }));
        }
      } catch (error) {
        console.error('❌ Network error:', error);
        // ✅ GEÄNDERT: Übersetzt
        alert(t('sidebar.errors.reorderError', { error: error.message }));
      }

      setDraggedGroup(null);
    };

    return (
      <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
        {/* All Screens */}
        <div className="mb-4">
          <button
            onClick={() => onSelectGroup(null)}
            className={`w-full text-left p-3 rounded transition-colors ${!activeGroup
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
          >
            <div className="flex items-center justify-between">
              {/* ✅ GEÄNDERT: Übersetzt */}
              <span className="font-semibold">{t('sidebar.allScreens')}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">({totalScreens})</span>
            </div>
          </button>
        </div>

        {/* Groups */}
<div className="space-y-2">
  <div className="flex items-center justify-between mb-2">
    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      {t('sidebar.groups')}
    </h3>
    <button
      onClick={() => setShowCreateGroupModal(true)}
      className="text-primary-600 dark:text-primary-400 hover:text-blue-700 dark:hover:text-blue-300"
      title={t('sidebar.createGroup')}
    >
      <FolderPlus size={16} />
    </button>
  </div>

  {groups.length === 0 ? (
    <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
      {t('sidebar.noGroupsYet')}
    </p>
  ) : (
    groups.map(group => (
      <div
        key={group.id}
        className="relative"
        draggable={true}
        onDragStart={(e) => handleDragStart(group, e)}
        onDragOver={(e) => handleDragOver(group, e)}
        onDragLeave={handleDragLeave}
        onDragEnd={handleDragEnd}
        onDrop={(e) => handleDrop(group, e)}
        onMouseEnter={() => setHoveredGroup(group.id)}
        onMouseLeave={() => setHoveredGroup(null)}
      >
        <div
          onClick={() => onSelectGroup(group)}
          className={`w-full text-left p-3 rounded transition-all select-none ${activeGroup?.id === group.id
              ? 'bg-blue-100 dark:bg-blue-900'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            } ${dragOverGroup === group.id && draggedGroup?.id !== group.id
              ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/30'
              : ''
            } ${draggedGroup?.id === group.id
              ? 'opacity-40 scale-95'
              : ''
            }`}
          style={{
            borderLeft: `4px solid ${group.color}`,
            cursor: draggedGroup ? 'grabbing' : 'grab'
          }}
        >
          <div className="flex items-center justify-between pointer-events-none">
            <div className="flex items-center gap-2 flex-1 min-w-0">
  {renderGroupIcon(group.icon, 20, 'flex-shrink-0')}
  <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
    {group.name}
  </span>
</div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {screens.filter(s => s.group_id === group.id).length}
              </span>
              {screens.filter(s => 
                s.group_id === group.id && s.status === 'online'
              ).length > 0 && (
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              )}
            </div>
          </div>
        </div>

        {/* Edit & Delete Buttons */}
        {hoveredGroup === group.id && !draggedGroup && (
          <div className="absolute right-2 top-2 flex gap-1 bg-white dark:bg-gray-800 rounded shadow-lg p-1 z-10 border border-gray-200 dark:border-gray-600">
            <button
              onClick={(e) => handleEditGroup(group, e)}
              className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900 rounded text-primary-600 dark:text-primary-400"
              title={t('sidebar.editGroup')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={(e) => handleDeleteGroup(group.id, e)}
              className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-600 dark:text-red-400"
              title={t('sidebar.deleteGroup')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>
    ))
  )}
</div>

{/* Drag Hint */}
{groups.length > 1 && !draggedGroup && (
  <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-center">
    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
      🖱
      <span>{t('sidebar.dragToReorder')}</span>
    </p>
  </div>
)}

{/* Dragging Indicator */}
{draggedGroup && (
  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-lg animate-pulse">
    <p className="text-xs text-blue-700 dark:text-blue-300 font-semibold text-center">
      {t('sidebar.moving', { name: draggedGroup.name })}
    </p>
  </div>
)}

{/* Create Group Button */}
<button
  onClick={() => setShowCreateGroupModal(true)}
  className="w-full mt-4 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded hover:border-primary-500 dark:hover:border-blue-400 transition-colors flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400"
>
  <FolderPlus size={20} />
  <span className="text-sm font-medium">{t('sidebar.newGroup')}</span>
</button>
      </div>
    );
  };

  const SuperAdminView = () => {
    const { t } = useTranslation();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newOrg, setNewOrg] = useState({
      organization_name: '',
      organization_color: '#2563eb',
      admin_email: '',
      admin_password: '',
      admin_name: ''
    });

    // SuperAdmin: Load data ONCE
    useEffect(() => {
      const loadSuperAdminData = async () => {
        // Nur laden wenn noch nicht geladen UND user ist super_admin
        if (!hasLoadedSuperAdminData.current && currentUser?.role === 'super_admin') {
          hasLoadedSuperAdminData.current = true; // Markiere als geladen
          
          console.log('🔄 Loading SuperAdmin data...');
          await loadAllOrganizations();  // ← GEÄNDERT
          await loadAllUsers();
          console.log('✅ SuperAdmin data loaded');
        }
      };
      
      loadSuperAdminData();
    }, [currentUser?.role]); // Nur wenn role sich ändert

    const loadOrganizations = async () => {
      setLoading(true);
      try {
        // Prüfe erst NACH setLoading
        if (!currentUser || currentUser.role !== 'super_admin') {
          setOrganizations([]);
          return;
        }
        
        const savedToken = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/superadmin/organizations`, {
          headers: { Authorization: `Bearer ${savedToken}` }
        });

        if (!response.ok) {
          throw new Error('Failed to load organizations');
        }

        const data = await response.json();
        console.log('📊 Loaded organizations:', data);

        setOrganizations(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading organizations:', error);
        setOrganizations([]);
      } finally {
        setLoading(false); // Wird IMMER ausgeführt
      }
    };

    const loadAllUsers = async () => {
      if (!currentUser || currentUser.role !== 'super_admin') return;
      
      setLoadingUsers(true);
      try {
        const savedToken = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/superadmin/users`, {
          headers: { Authorization: `Bearer ${savedToken}` }
        });

        if (!response.ok) {
          throw new Error('Failed to load users');
        }

        const data = await response.json();
        console.log('👥 Loaded users:', data);
        setAllUsers(data);
      } catch (error) {
        console.error('Error loading users:', error);
        setAllUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    const createOrganization = async () => {
      if (!newOrg.organization_name || !newOrg.admin_email || !newOrg.admin_password) {
        alert('Please fill all required fields');
        return;
      }

      try {
        const savedToken = localStorage.getItem('token');

        // Debug: Log was wir senden
        console.log('📤 Sending to backend:', newOrg);

        const response = await fetch(`${API_URL}/superadmin/organizations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${savedToken}`
          },
          body: JSON.stringify(newOrg)  // ⬅ Das sollte richtig sein
        });

        console.log('📥 Response status:', response.status);
        const responseData = await response.json();
        console.log('📥 Response data:', responseData);

        if (!response.ok) {
          throw new Error(responseData.error || 'Failed to create organization');
        }

        await loadOrganizations();
        setShowCreateModal(false);
        setNewOrg({
          organization_name: '',
          organization_color: '#2563eb',
          admin_email: '',
          admin_password: '',
          admin_name: ''
        });
        alert(t('dashboard.confirmations.orgCreated'));
      } catch (error) {
        console.error('❌ Error:', error);
        alert('Error: ' + error.message);
      }
    };

    const deleteOrganization = async (id) => {
      if (!window.confirm(t('dashboard.confirmations.deleteOrganization'))) {
        return;
      }

      try {
        const savedToken = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/superadmin/organizations/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${savedToken}` }
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to delete');
        }

        await loadOrganizations();
        alert(t('dashboard.confirmations.orgDeleted'));
      } catch (error) {
        alert('Error: ' + error.message);
      }
    };

    const extendTrial = async (orgId, orgName) => {
      const days = prompt(`How many days to extend trial for "${orgName}"?\n\nOptions: 7, 14, 30, 60, 90`);
      
      if (!days) return; // User cancelled
      
      const daysNum = parseInt(days);
      if (isNaN(daysNum) || daysNum < 1 || daysNum > 365) {
        alert(t('dashboard.confirmations.validDaysPrompt'));
        return;
      }

      try {
        const savedToken = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/superadmin/organizations/${orgId}/extend-trial`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${savedToken}`
          },
          body: JSON.stringify({ days: daysNum })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to extend trial');
        }

        const result = await response.json();
        await loadOrganizations(); // Reload to show new date
        alert(`✅ Trial extended!\n\n${result.message}\nNew end date: ${new Date(result.newTrialEnd).toLocaleDateString()}`);
      } catch (error) {
        alert('❌ Error: ' + error.message);
      }
    };

    const resetUserPassword = async (userId, userEmail, userName) => {
      const newPassword = prompt(
        `Reset password for: ${userName} (${userEmail})\n\n` +
        `Enter new password (min. 8 characters):`
      );

      if (!newPassword) return; // User cancelled

      if (newPassword.length < 8) {
        alert('❌ Password must be at least 8 characters long!');
        return;
      }

      try {
        const savedToken = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/superadmin/users/${userId}/reset-password`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${savedToken}`
          },
          body: JSON.stringify({ newPassword })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to reset password');
        }

        const result = await response.json();
        alert(`✅ Password reset successfully!\n\nUser: ${result.user.name}\nEmail: ${result.user.email}\n\nNew password: ${newPassword}\n\n⚠️ Make sure to share this password securely with the user!`);
      } catch (error) {
        alert('❌ Error: ' + error.message);
      }
    };

    return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            👑 {t('superAdmin.superAdminPanel')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('superAdmin.manageOrganizations')}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
        >
          <Plus size={20} /> {t('superAdmin.createOrganization')}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-4">{t('common.loading')}...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allOrganizations.map(org => (
            <div key={org.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                    style={{ backgroundColor: org.color || '#2563eb' }}
                  >
                    {org.logo_url ? (
                      <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch'}${org.logo_url}`} alt="" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      org.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{org.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">ID: {org.id}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => extendTrial(org.id, org.name)}
                    className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                    title={t('superAdmin.extendTrial')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => deleteOrganization(org.id)}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title={t('common.delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('superAdmin.users')}:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{org.user_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('superAdmin.screens')}:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{org.screen_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('superAdmin.media')}:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{org.media_count || 0}</span>
                </div>
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('superAdmin.createdDate')}: {new Date(org.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* User Management Section */}
      <div className="mt-8">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
          👥 {t('superAdmin.userManagement')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('superAdmin.manageUsersAcrossOrgs')}
        </p>

        {loadingUsers ? (
          <div className="text-center py-8">
            <div className="inline-block w-6 h-6 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('superAdmin.organization')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {allUsers.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: user.organization_color || '#2563eb' }}
                        >
                          {user.organization_name?.charAt(0).toUpperCase()}
                        </div>
                        <span 
  className="px-2 py-0.5 text-xs rounded-full font-medium text-white shadow-sm"
  style={{ 
    backgroundColor: user.organization_color || '#2563eb'
  }}
>
  🏢 {user.organization_name}
</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === 'super_admin' 
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          : user.role === 'admin'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {user.role === 'super_admin' 
                          ? t('users.superAdmin')
                          : user.role === 'admin'
                          ? t('users.admin')
                          : t('users.user')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.email_verified
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {user.email_verified ? t('users.verified') : t('superAdmin.unverified')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => resetUserPassword(user.id, user.email, user.name)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title={t('users.resetPassword')}
                      >
                        🔑 {t('users.resetPassword')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
              {t('superAdmin.createOrganization')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  {t('superAdmin.newOrgName')} *
                </label>
                <input
                  type="text"
                  value={newOrg.organization_name}
                  onChange={(e) => setNewOrg({ ...newOrg, organization_name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder={t('superAdmin.orgNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  {t('superAdmin.brandColor')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={newOrg.organization_color}
                    onChange={(e) => setNewOrg({ ...newOrg, organization_color: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={newOrg.organization_color}
                    onChange={(e) => setNewOrg({ ...newOrg, organization_color: e.target.value })}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {t('superAdmin.firstAdminUser')}
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                      {t('superAdmin.adminEmail')} *
                    </label>
                    <input
                      type="email"
                      value={newOrg.admin_email}
                      onChange={(e) => setNewOrg({ ...newOrg, admin_email: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder={t('superAdmin.adminEmailPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                      {t('superAdmin.adminName')}
                    </label>
                    <input
                      type="text"
                      value={newOrg.admin_name}
                      onChange={(e) => setNewOrg({ ...newOrg, admin_name: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder={t('superAdmin.adminNamePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                      {t('superAdmin.adminPassword')} *
                    </label>
                    <input
                      type="password"
                      value={newOrg.admin_password}
                      onChange={(e) => setNewOrg({ ...newOrg, admin_password: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder={t('superAdmin.passwordMinLength')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={createOrganization}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
              >
                {t('common.create')}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors sticky top-0 z-40">
  <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 md:py-4">
          <div className="flex items-center justify-between">
            {/* Left Side - Organization & Platform Name */}
            <div className="flex items-center gap-4">
              {/* Organization Info / Switcher */}
              <div className="flex items-center gap-3 pr-4 border-r border-gray-300 dark:border-gray-600">
                {currentUser?.role === 'super_admin' ? (
                  // Super Admin: Organization Switcher
                  <div className="relative">
                    <button
                      onClick={() => setShowOrgSwitcher(!showOrgSwitcher)}
                      className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 px-3 py-2 rounded-lg transition-colors"
                    >
                      {/* Logo */}
                      <div
                        className="w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm md:text-lg shadow-md"
                        style={{
                          backgroundColor: viewingAsOrganization
                            ? (viewingAsOrganization.color || '#2563eb')
                            : (currentUser.organization?.color || '#2563eb')
                        }}
                      >
                        {viewingAsOrganization?.logo_url || currentUser.organization?.logo_url ? (
                          <img
                            src={`${API_BASE_URL}${viewingAsOrganization?.logo_url || currentUser.organization?.logo_url}`}
                            alt="Logo"
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          (viewingAsOrganization?.name || currentUser.organization?.name || 'O').charAt(0).toUpperCase()
                        )}
                      </div>

                      {/* Organization Name */}
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="hidden sm:inline font-semibold text-gray-800 dark:text-gray-100">
  {viewingAsOrganization?.name || currentUser.organization?.name}
</span>
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        {viewingAsOrganization && (
                          <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full font-medium">
                            👁️ Viewing {viewingAsOrganization.name}
                          </span>
                        )}
                      </div>
                    </button>

                    {/* Dropdown Menu */}
{showOrgSwitcher && (
  <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        🔄 {t('organization.switchOrganization')}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {t('organization.viewAndManage')}
      </p>
    </div>

    {/* Own Organization */}
    {viewingAsOrganization && (
      <>
        <button
          onClick={switchBackToOwnOrganization}
          className="w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left border-b border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold shadow-md"
              style={{ backgroundColor: currentUser.organization?.color || '#2563eb' }}
            >
              {currentUser.organization?.logo_url ? (
                <img
                  src={`${API_BASE_URL}${currentUser.organization.logo_url}`}
                  alt=""
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                (currentUser.organization?.name || 'O').charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {currentUser.organization?.name}
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400">
                ⬅ {t('organization.backToYourOrg')}
              </p>
            </div>
          </div>
        </button>
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            {t('organization.otherOrganizations').toUpperCase()}
          </p>
        </div>
      </>
    )}

    {/* All Organizations */}
    {Array.isArray(allOrganizations) && allOrganizations.map(org => (
      <button
        key={org.id}
        onClick={() => switchToOrganization(org)}
        className={`w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${
          viewingAsOrganization?.id === org.id ? 'bg-purple-50 dark:bg-purple-900/20' : ''
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold shadow-md"
            style={{ backgroundColor: org.color || '#2563eb' }}
          >
            {org.logo_url ? (
              <img
                src={`${API_BASE_URL}${org.logo_url}`}
                alt=""
                className="w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              org.name.charAt(0).toUpperCase()
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {org.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('organization.screensCount', { count: org.screen_count || 0 })} · {t('organization.usersCount', { count: org.user_count || 0 })}
            </p>
          </div>
          {viewingAsOrganization?.id === org.id && (
            <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
          )}
        </div>
      </button>
    ))}

    {/* Show message if no organizations */}
    {(!Array.isArray(allOrganizations) || allOrganizations.length === 0) && (
      <div className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
        {t('organization.noOtherOrgs')}
      </div>
    )}
  </div>
)}
                  </div>
                ) : (
                  // Normal Admin/User: Just show organization
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-md"
                      style={{ backgroundColor: currentUser.organization?.color || '#2563eb' }}
                    >
                      {currentUser.organization?.logo_url ? (
                        <img
                          src={`${API_BASE_URL}${currentUser.organization.logo_url}`}
                          alt="Logo"
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        (currentUser.organization?.name || 'O').charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                      {currentUser.organization?.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Theme Toggle */}
<ThemeToggle />

{/* Language Switcher */}
<LanguageSwitcher />

{/* Settings Button - Nur für Admins und Super Admins - NUR ICON */}
{(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
  <button
    onClick={() => {
      console.log('⚙️ Settings button clicked');
      
      // Wenn Super Admin eine andere Org anschaut, verwende diese
      if (currentUser?.role === 'super_admin' && viewingAsOrganization) {
        console.log('👑 Super Admin viewing org:', viewingAsOrganization);
        
        // Create a temporary user object with the viewing organization
        const tempUser = {
          ...currentUser,
          organization: viewingAsOrganization
        };
        
        setSettingsUser(tempUser);
        setShowSettings(true);
      } else {
        // Normal case: use current user's organization
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const freshUser = JSON.parse(userStr);
          console.log('📋 Loaded fresh user:', freshUser);
          setSettingsUser(freshUser);
          setShowSettings(true);
        }
      }
    }}
    className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
    title={t('nav.settings')}
  >
    <Settings size={18} />
  </button>
)}

{/* Logout Button - NUR ICON */}
<button
  onClick={handleLogout}
  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
  title={t('nav.logout')}
>
  <LogOut size={18} />
</button>

{/* Divider - nur Desktop */}
<div className="hidden lg:block h-8 w-px bg-gray-300 dark:bg-gray-600"></div>

{/* AlpenScreen Branding - nur Desktop */}
<img 
  src="/assets/logo/alpenscreen-ultra-no-subtitle-transparent-512px.png" 
  alt="AlpenScreen"
  className="hidden lg:block h-16 opacity-90 hover:opacity-100 transition-opacity"
/>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors sticky top-[52px] md:top-[72px] z-30">
  <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Desktop Navigation - versteckt auf Mobile */}
<div className="hidden md:flex gap-8">
  {/* Dashboard */}
  <button
    onClick={() => setActiveTab('dashboard')}
    className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
      activeTab === 'dashboard'
        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
    }`}
  >
    <LayoutDashboard size={18} />
    {t('nav.dashboard')}
  </button>

  {/* Media */}
  <button
    onClick={() => setActiveTab('media')}
    className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
      activeTab === 'media'
        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
    }`}
  >
    <Images size={18} />
    {t('nav.media')}
  </button>

  {/* Playlists */}
  <button
    onClick={() => setActiveTab('playlists')}
    className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
      activeTab === 'playlists'
        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
    }`}
  >
    <ListVideo size={18} />
    {t('nav.playlists')}
  </button>

  {/* Scheduler */}
  <button
    onClick={() => setActiveTab('scheduler')}
    className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
      activeTab === 'scheduler'
        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
    }`}
  >
    <Calendar size={18} />
    {t('nav.scheduler')}
  </button>

 {/* Analytics - nur für Admin (Business/Enterprise) oder Super Admin */}
{(
  (localStorage.getItem('userRole') === 'admin' && 
   (localStorage.getItem('subscriptionTier') === 'business' || 
    localStorage.getItem('subscriptionTier') === 'enterprise')) ||
  localStorage.getItem('userRole') === 'super_admin'
) && (
  <button
    onClick={() => setActiveTab('analytics')}
    className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
      activeTab === 'analytics'
        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
    }`}
  >
    <BarChart3 size={18} />
    {t('nav.analytics')}
  </button>
)}

  {/* Marketplace - nur für Admins */}
  {(localStorage.getItem('userRole') === 'admin' || 
    localStorage.getItem('userRole') === 'super_admin') && (
    <button
      onClick={() => setActiveTab('marketplace')}
      className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
        activeTab === 'marketplace'
          ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
      }`}
    >
      <Store size={18} />
      {t('nav.marketplace')}
    </button>
  )}

  {/* Widgets - nur für Admins */}
  {(localStorage.getItem('userRole') === 'admin' || 
    localStorage.getItem('userRole') === 'super_admin') && (
    <button
      onClick={() => setActiveTab('widgets')}
      className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
        activeTab === 'widgets'
          ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
      }`}
    >
      <Zap size={18} />
      {t('nav.widgets')}
    </button>
  )}

{/* Content Studio - nur für Admins */}
{(localStorage.getItem('userRole') === 'admin' || 
  localStorage.getItem('userRole') === 'super_admin') && (
  <button
    onClick={() => setActiveTab('content-studio')}
    className={`py-4 px-2 border-b-2 font-medium transition-colors flex items-center gap-2 ${
      activeTab === 'content-studio'
        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
    }`}
  >
    <Sparkles size={18} />
    {t('nav.contentStudio', 'Content Studio')}
  </button>
)}

  {/* Benutzer Tab - NUR für normale Admins (nicht SuperAdmin) */}
{canManageUsers && userRole !== 'super_admin' && (
  <button
    onClick={() => setActiveTab('users')}
    className={`
      flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all
      ${activeTab === 'users'
        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/50'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
      }
    `}
  >
    <Users size={20} />
    <span className="hidden md:inline">{t('nav.users')}</span>
  </button>
)}

{userRole === 'super_admin' && (
  <button
    onClick={() => setActiveTab('superadmin')}
    className={`
      flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all
      ${activeTab === 'superadmin'
        ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/50'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
      }
    `}
  >
    <Crown size={20} />
    <span className="hidden md:inline">Super Admin</span>
  </button>
)}

</div>
          {/* Mobile Navigation Component */}
          <MobileNav 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            canManageUsers={canManageUsers}
            userRole={localStorage.getItem('userRole')}
            t={t}
          />
        </div>
      </nav>

      <main className="dashboard-content max-w-7xl mx-auto px-4 md:px-6 pt-4 md:pt-8 pb-32 md:pb-8">
  {activeTab === 'dashboard' && <DashboardView trialInfo={trialInfo} />}
  {activeTab === 'media' && <EnhancedMediaLibrary currentUser={currentUser} />}
  {activeTab === 'playlists' && <PlaylistManager />}
  {activeTab === 'scheduler' && <AdvancedScheduler currentUser={currentUser} />}
  {activeTab === 'analytics' && <Analytics />}
  {activeTab === 'marketplace' && <FeatureMarketplace />}
  {activeTab === 'widgets' && <WidgetManager />}
{activeTab === 'content-studio' && (localStorage.getItem('userRole') === 'admin' || localStorage.getItem('userRole') === 'super_admin') && <ContentStudio />}

  {/* SuperAdmin Panel - Alle SuperAdmin Features in einem */}
  {activeTab === 'superadmin' && userRole === 'super_admin' && (
    <SuperAdminPanel />
  )}
  
  {/* Benutzer-Tab - NUR für normale Admins */}
  {activeTab === 'users' && canManageUsers && userRole !== 'super_admin' && (
    <UsersView />
  )}
</main>

      {showAddUser && (
  <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 md:p-0 modal-backdrop">
    <div className="bg-white dark:bg-gray-800 rounded-lg md:rounded-lg p-0 md:p-6 w-full max-w-md transition-colors modal-container">
      
      {/* Header - sticky on mobile */}
      <div className="modal-header md:mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Add New User
          </h3>
          <button
            onClick={() => {
              setShowAddUser(false);
              setNewUser({ email: '', password: '', name: '', role: 'user' });
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content - scrollable */}
      <div className="modal-content px-4 md:px-0">
        <div className="space-y-4">
          <div className="form-group">
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              Email *
            </label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
              placeholder="user@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              Name
            </label>
            <input
              type="text"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
              placeholder="John Doe"
            />
          </div>

          <div className="form-group">
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              Password *
            </label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
              placeholder="••••••••"
              required
              minLength={8}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Min. 8 characters
            </p>
          </div>

          <div className="form-group">
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              Role
            </label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
      </div>

      {/* Footer - sticky on mobile */}
      <div className="modal-footer md:mt-6">
        <div className="flex flex-col-reverse md:flex-row gap-3">
          <button
            onClick={() => {
              setShowAddUser(false);
              setNewUser({ email: '', password: '', name: '', role: 'user' });
            }}
            className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-3 md:py-2 rounded-lg transition-colors font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              try {
                const savedToken = localStorage.getItem('token');
                const viewingOrgId = localStorage.getItem('viewingAsOrgId');
                const userRole = localStorage.getItem('userRole');
                
                // Build URL with filter_org if viewing different organization
                let url = `${API_URL}/users`;
                if (viewingOrgId && userRole === 'super_admin') {
                  url += `?filter_org=${viewingOrgId}`;
                }
                
                console.log('🔹 Creating user:', newUser);
                console.log('   🔍 URL:', url);
                
                const response = await fetch(url, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${savedToken}`
                  },
                  body: JSON.stringify(newUser)
                });

                if (response.ok) {
                  const createdUser = await response.json();
                  console.log('User created:', createdUser);
                  setShowAddUser(false);
                  setNewUser({ email: '', password: '', name: '', role: 'user' });
                  loadData();
                } else {
                  const error = await response.json();
                  console.error('❌ Failed to create user:', error);
                  alert(error.error || 'Failed to create user');
                }
              } catch (error) {
                console.error('❌ Network error:', error);
                alert('Failed to create user: ' + error.message);
              }
            }}
            className="flex-1 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-3 md:py-2 rounded-lg transition-colors font-semibold"
          >
            Create User
          </button>
        </div>
      </div>
      
    </div>
  </div>
)}

      {/* Organization Settings Modal - KOMPLETT NEU */}
      {showSettings ? (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Organization Settings
                </h2>
                {currentUser?.role === 'super_admin' && viewingAsOrganization && (
                  <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                    ✏️ Editing: {viewingAsOrganization.name}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <OrganizationSettings
                key={`org-settings-${Date.now()}`}
                currentUser={settingsUser}
                onUpdate={(updatedOrg) => {
                  console.log('📥 onUpdate called:', updatedOrg);

                  // Update localStorage
                  const user = JSON.parse(localStorage.getItem('user'));
                  user.organization = updatedOrg;
                  localStorage.setItem('user', JSON.stringify(user));

                  // Update state
                  setCurrentUser({ ...currentUser, organization: updatedOrg });

                  // Reload page
                  setTimeout(() => {
                    setShowSettings(false);
                    window.location.reload();
                  }, 1000);
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {showAddMediaModal && (
  <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 md:p-0 modal-backdrop">
    <div className="bg-white dark:bg-gray-800 rounded-lg md:rounded-lg p-0 md:p-6 w-full max-w-md transition-colors modal-container">
      
      {/* Header - sticky on mobile */}
      <div className="modal-header md:mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t('media.addMedia')}
          </h3>
          <button
            onClick={() => {
              setShowAddMediaModal(false);
              setSelectedFile(null);
              setUploadMethod('url');
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>
      
      {/* Content - scrollable */}
      <div className="modal-content px-4 md:px-0">
        {/* Upload method toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setUploadMethod('file')}
            className={`flex-1 px-4 py-3 md:py-2 rounded-lg flex items-center justify-center gap-2 transition-colors font-semibold ${
              uploadMethod === 'file'
                ? 'bg-primary-600 dark:bg-primary-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <Upload size={18} />
            <span className="text-sm md:text-base">{t('media.uploadFile')}</span>
          </button>
          <button
            onClick={() => setUploadMethod('url')}
            className={`flex-1 px-4 py-3 md:py-2 rounded-lg flex items-center justify-center gap-2 transition-colors font-semibold ${
              uploadMethod === 'url'
                ? 'bg-primary-600 dark:bg-primary-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <Globe size={18} />
            <span className="text-sm md:text-base">{t('media.fromURL')}</span>
          </button>
        </div>

        <div className="space-y-4">
          {uploadMethod === 'file' ? (
            <>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  {t('media.chooseFile')}
                </label>
                <input
                  type="file"
                  accept="image/*,video/*,.pdf"
                  onChange={handleFileSelect}
                  className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg px-4 py-4 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900 dark:file:text-primary-300"
                />
                {selectedFile && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      ✓ {selectedFile.name}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={newMedia.name}
                  onChange={(e) => setNewMedia({ ...newMedia, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                  placeholder="Auto-filled from filename"
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  {t('media.name')}
                </label>
                <input
                  type="text"
                  value={newMedia.name}
                  onChange={(e) => setNewMedia({ ...newMedia, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                  placeholder="My Media"
                />
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  {t('media.type')}
                </label>
                <select
                  value={newMedia.type}
                  onChange={(e) => setNewMedia({ ...newMedia, type: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                >
                  <option value="image">{t('media.image')}</option>
                  <option value="video">{t('media.video')}</option>
                  <option value="webpage">{t('media.webpage')}</option>
                  <option value="pdf">{t('media.pdf')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  {t('media.url')}
                </label>
                <input
                  type="text"
                  value={newMedia.url}
                  onChange={(e) => setNewMedia({ ...newMedia, url: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                  placeholder="https://..."
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              {t('media.duration')}
            </label>
            <input
              type="number"
              value={newMedia.duration}
              onChange={(e) => setNewMedia({ ...newMedia, duration: parseInt(e.target.value) })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 md:py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
              placeholder="10"
              min="1"
            />
          </div>
        </div>
      </div>
      
      {/* Footer - sticky on mobile */}
      <div className="modal-footer md:mt-6">
        <div className="flex flex-col-reverse md:flex-row gap-3">
          <button
            onClick={() => {
              setShowAddMediaModal(false);
              setSelectedFile(null);
              setUploadMethod('url');
            }}
            className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-3 md:py-2 rounded-lg transition-colors font-semibold"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={addMedia}
            disabled={uploading || (uploadMethod === 'file' ? !selectedFile : !newMedia.name || !newMedia.url)}
            className="flex-1 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-3 md:py-2 rounded-lg disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {uploading ? t('media.uploading') : (uploadMethod === 'file' ? t('media.upload') : t('media.addMedia'))}
          </button>
        </div>
      </div>
      
    </div>
  </div>
)}

   {/* Add Screen Modal */}
<AddScreenModal
  isOpen={showAddScreenModal}
  onClose={() => setShowAddScreenModal(false)}
  onScreenAdded={(responseData) => {
    // Zeige Connection Info Modal
    setNewlyCreatedScreen(responseData.screen);
    setShowConnectionModal(true);
    
    // Refresh screens
    loadData();
  }}
/>

      {/* QR Code Modal */}
      {showQRModal && (
        <QRCodeModal
          screen={selectedScreenForQR}
          onClose={() => {
            setShowQRModal(false);
            setSelectedScreenForQR(null);
          }}
        />
      )}

      <EditScreenModal
  screen={editingScreen}
  isOpen={!!editingScreen}
  onClose={() => setEditingScreen(null)}
  onSave={handleSaveScreen}
  groups={groups}
  playlists={playlists}
  t={t}
/>


      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <GroupModal
          group={null}
          token={token}
          onClose={() => setShowCreateGroupModal(false)}
          onSave={() => {
            setShowCreateGroupModal(false);
            loadData();
          }}
          loadData={loadData}
        />
      )}

      {/* Edit Group Modal */}
      {showEditGroupModal && editingGroup && (
        <GroupModal
          group={editingGroup}
          token={token}
          onClose={() => {
            setShowEditGroupModal(false);
            setEditingGroup(null);
          }}
          onSave={() => {
            setShowEditGroupModal(false);
            setEditingGroup(null);
            loadData();
          }}
          loadData={loadData}
        />
      )}

      {/* Connection Info Modal */}
{showConnectionModal && newlyCreatedScreen && (
  <ScreenConnectionModal
    isOpen={showConnectionModal}
    onClose={() => {
      setShowConnectionModal(false);
      setNewlyCreatedScreen(null);
    }}
    screen={newlyCreatedScreen}
  />
)}
{/* Setup Wizard for new users */}
{showSetupWizard && (
  <SetupWizard
    token={token}
    onComplete={() => {
      setShowSetupWizard(false);
      loadData();
    }}
    onSkip={() => setShowSetupWizard(false)}
  />
)}

 <ToastContainer toasts={toasts} removeToast={removeToast} />

    </div>
  );
};

export default Dashboard;